import React, { useEffect, useState } from 'react';
import { FeelIcon, NeedIcon, HeartIcon } from './BespokeIcons';

/**
 * SpiralInterchange - Beautiful visualization of the eternal dance between feelings and needs
 * Shows how red (feelings) always relates to green/blue (needs) - met or unmet
 * The infinite spiral represents the continuous interchange in human connection
 */
export const SpiralInterchange = ({ 
  yourFeelings, 
  yourNeeds, 
  theirFeelings, 
  theirNeeds,
  intensity = 'medium',
  mode = 'conflict' // 'conflict', 'appreciation', 'romance'
}) => {
  const [rotation, setRotation] = useState(0);
  const [pulse, setPulse] = useState(1);
  
  // Continuous spiral rotation
  useEffect(() => {
    const interval = setInterval(() => {
      setRotation(r => (r + 0.5) % 360);
      setPulse(p => p === 1 ? 1.05 : 1);
    }, 50);
    return () => clearInterval(interval);
  }, []);
  
  // Calculate connection strength based on content
  const connectionStrength = () => {
    const hasYourData = yourFeelings && yourNeeds;
    const hasTheirData = theirFeelings || theirNeeds;
    if (hasYourData && hasTheirData) return 'strong';
    if (hasYourData || hasTheirData) return 'partial';
    return 'emerging';
  };
  
  const strength = connectionStrength();
  
  // Color theming based on mode
  const colors = {
    conflict: {
      feeling: '#EF4444',
      need: '#10B981',
      spiral: '#FBBF24'
    },
    appreciation: {
      feeling: '#FB7185',
      need: '#34D399',
      spiral: '#F59E0B'
    },
    romance: {
      feeling: '#F43F5E',
      need: '#EC4899',
      spiral: '#F472B6'
    }
  };
  
  const theme = colors[mode] || colors.conflict;
  
  return (
    <div className="relative w-full py-6 px-4 bg-gradient-to-br from-black/40 to-zinc-900/40 rounded-xl border border-amber-500/20 overflow-hidden">
      {/* Background animated spiral */}
      <div 
        className="absolute inset-0 opacity-10"
        style={{
          background: `radial-gradient(circle at 50% 50%, ${theme.spiral}40 0%, transparent 70%)`,
          transform: `rotate(${rotation}deg) scale(${pulse})`,
          transition: 'transform 0.05s linear'
        }}
      />
      
      {/* Main content */}
      <div className="relative z-10">
        {/* Title */}
        <div className="text-center mb-6">
          <h3 className="text-amber-400/80 text-sm font-medium mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
            The Spiral of Connection
          </h3>
          <p className="text-white/40 text-xs">
            Feelings always relate to needs — met or unmet
          </p>
        </div>
        
        {/* Spiral visualization */}
        <div className="flex items-center justify-between mb-6 relative">
          {/* Your side */}
          <div className="flex flex-col items-center gap-3 flex-1">
            <div 
              className="w-16 h-16 rounded-full flex items-center justify-center relative"
              style={{ 
                background: `radial-gradient(circle, ${theme.feeling}30, ${theme.feeling}10)`,
                boxShadow: yourFeelings ? `0 0 20px ${theme.feeling}40` : 'none',
                animation: yourFeelings ? 'pulse 2s ease-in-out infinite' : 'none'
              }}
            >
              <FeelIcon size={24} color={theme.feeling} />
              {yourFeelings && (
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-rose-500 rounded-full animate-ping" />
              )}
            </div>
            <span className="text-rose-300/80 text-xs font-medium">Your Feelings</span>
            
            <div className="h-8 w-px bg-gradient-to-b from-transparent via-rose-500/50 to-transparent" />
            
            <div 
              className="w-16 h-16 rounded-full flex items-center justify-center relative"
              style={{ 
                background: `radial-gradient(circle, ${theme.need}30, ${theme.need}10)`,
                boxShadow: yourNeeds ? `0 0 20px ${theme.need}40` : 'none',
                animation: yourNeeds ? 'pulse 2s ease-in-out infinite 0.5s' : 'none'
              }}
            >
              <NeedIcon size={24} color={theme.need} />
              {yourNeeds && (
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-500 rounded-full animate-ping" style={{ animationDelay: '0.5s' }} />
              )}
            </div>
            <span className="text-emerald-300/80 text-xs font-medium">Your Needs</span>
          </div>
          
          {/* Center spiral */}
          <div className="flex-shrink-0 mx-4 relative">
            <svg width="80" height="120" viewBox="0 0 80 120" className="relative z-10">
              {/* Animated spiral path */}
              <defs>
                <linearGradient id="spiralGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                  <stop offset="0%" stopColor={theme.feeling} stopOpacity="0.6" />
                  <stop offset="50%" stopColor={theme.spiral} stopOpacity="0.8" />
                  <stop offset="100%" stopColor={theme.need} stopOpacity="0.6" />
                </linearGradient>
                
                <filter id="glow">
                  <feGaussianBlur stdDeviation="2" result="coloredBlur"/>
                  <feMerge>
                    <feMergeNode in="coloredBlur"/>
                    <feMergeNode in="SourceGraphic"/>
                  </feMerge>
                </filter>
              </defs>
              
              {/* Flowing connection lines */}
              <path 
                d="M 20 20 Q 40 40, 40 60 T 60 100"
                stroke="url(#spiralGradient)"
                strokeWidth="2"
                fill="none"
                strokeLinecap="round"
                filter="url(#glow)"
                style={{
                  strokeDasharray: '100',
                  strokeDashoffset: rotation * -2,
                  opacity: strength === 'strong' ? 1 : strength === 'partial' ? 0.6 : 0.3
                }}
              />
              
              {/* Mirror spiral */}
              <path 
                d="M 60 20 Q 40 40, 40 60 T 20 100"
                stroke="url(#spiralGradient)"
                strokeWidth="2"
                fill="none"
                strokeLinecap="round"
                filter="url(#glow)"
                style={{
                  strokeDasharray: '100',
                  strokeDashoffset: rotation * 2,
                  opacity: strength === 'strong' ? 1 : strength === 'partial' ? 0.6 : 0.3
                }}
              />
              
              {/* Center heart when connection is strong */}
              {strength === 'strong' && (
                <g transform="translate(40, 60)">
                  <HeartIcon size={16} color={theme.spiral} />
                </g>
              )}
            </svg>
            
            {/* Orbiting particles */}
            {strength !== 'emerging' && [0, 1, 2].map((i) => (
              <div
                key={i}
                className="absolute w-1.5 h-1.5 rounded-full"
                style={{
                  background: theme.spiral,
                  left: '50%',
                  top: '50%',
                  transform: `rotate(${rotation + i * 120}deg) translateX(30px) rotate(-${rotation + i * 120}deg)`,
                  boxShadow: `0 0 8px ${theme.spiral}`,
                  opacity: 0.8
                }}
              />
            ))}
          </div>
          
          {/* Their side */}
          <div className="flex flex-col items-center gap-3 flex-1">
            <div 
              className="w-16 h-16 rounded-full flex items-center justify-center relative"
              style={{ 
                background: `radial-gradient(circle, ${theme.feeling}30, ${theme.feeling}10)`,
                boxShadow: theirFeelings ? `0 0 20px ${theme.feeling}40` : 'none',
                animation: theirFeelings ? 'pulse 2s ease-in-out infinite 1s' : 'none',
                opacity: 0.7
              }}
            >
              <FeelIcon size={24} color={theme.feeling} />
              {theirFeelings && (
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-purple-500 rounded-full animate-ping" style={{ animationDelay: '1s' }} />
              )}
            </div>
            <span className="text-purple-300/80 text-xs font-medium">Their Feelings</span>
            
            <div className="h-8 w-px bg-gradient-to-b from-transparent via-purple-500/50 to-transparent" />
            
            <div 
              className="w-16 h-16 rounded-full flex items-center justify-center relative"
              style={{ 
                background: `radial-gradient(circle, ${theme.need}30, ${theme.need}10)`,
                boxShadow: theirNeeds ? `0 0 20px ${theme.need}40` : 'none',
                animation: theirNeeds ? 'pulse 2s ease-in-out infinite 1.5s' : 'none',
                opacity: 0.7
              }}
            >
              <NeedIcon size={24} color={theme.need} />
              {theirNeeds && (
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-cyan-500 rounded-full animate-ping" style={{ animationDelay: '1.5s' }} />
              )}
            </div>
            <span className="text-cyan-300/80 text-xs font-medium">Their Needs</span>
          </div>
        </div>
        
        {/* Connection status */}
        <div className="text-center mt-4">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-black/30 border border-amber-500/20">
            <div 
              className="w-2 h-2 rounded-full"
              style={{
                background: strength === 'strong' ? theme.spiral : strength === 'partial' ? theme.need : theme.feeling,
                boxShadow: `0 0 8px ${strength === 'strong' ? theme.spiral : theme.need}`,
                animation: 'pulse 2s ease-in-out infinite'
              }}
            />
            <span className="text-xs text-white/60">
              {strength === 'strong' && 'Universal Empathy Emerging'}
              {strength === 'partial' && 'Connection Forming'}
              {strength === 'emerging' && 'Begin the Spiral...'}
            </span>
          </div>
        </div>
        
        {/* Wisdom footer */}
        <div className="mt-6 p-3 bg-black/20 rounded-lg border border-white/5">
          <p className="text-white/50 text-xs text-center italic leading-relaxed" style={{ fontFamily: "'Playfair Display', serif" }}>
            {mode === 'romance' 
              ? '"In intimacy, feelings and needs dance together in infinite spiral — vulnerability creates connection"'
              : mode === 'appreciation'
              ? '"Gratitude flows when met needs spark joyful feelings — a spiral of celebration"'
              : '"Every feeling points to a need — follow the spiral to find the gold"'
            }
          </p>
        </div>
      </div>
      
      <style>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); opacity: 1; }
          50% { transform: scale(1.05); opacity: 0.8; }
        }
      `}</style>
    </div>
  );
};

export default SpiralInterchange;
